import "./dashboard.scss";
import { Suspense, useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { SectionHeader } from "@/sprout/typography";
import { usePage } from "@inertiajs/react";

import ProfileCard from "@components/profile_card";
import Applications from "@/components/dashboard/widgets/applications_widget";
import SearchSection from "@/components/dashboard/search_section.tsx";
import DashboardUploadResumeModal from "@components/dashboard/dashboard_upload_resume_modal";
import OnboardingWelcomeModal from "@components/onboarding/welcome_modal";
import { type RecentSearch } from "@/types/job_search/recent_searches";

import useDevice from "@/hooks/use_device";
import useTranslation from "@/hooks/use_translation";
import useProfile from "@/queries/use_profile";
import Fingerprint15 from "../images/fingerprint_15.svg";
import { SurveyCallout } from "@components/survey/survey_callout";
import DreamJobBanner, { type DreamJobBannerState } from "@components/dashboard/dream_job_banner";
import { useEffectOnce } from "react-use";
import JobSearchWidget from "@components/dashboard/widgets/job_search_widget.tsx";
import JobRecommendationsSection from "@components/dashboard/job_recommendations_section.tsx";
import IdentityVerificationBanner from "@components/dashboard/identity_verification_banner";

type PageProps = {
  uploadResumeModal?: boolean;
};

type Props = {
  firstName?: string;
  paths: {
    notificationSchedulePath: string;
  };
  closeOnLoad?: boolean;
  showWelcomeModal?: boolean;
  newUser: boolean;
  landedDreamJob?: boolean;
  recentSearches: RecentSearch[];
  dreamJobBanner?: { state: DreamJobBannerState; count: number };
};

const MobileDashboard = ({ recentSearches }: Props) => {
  return (
    <>
      <JobSearchWidget recentSearches={recentSearches} />
      <ProfileCard />
      <JobRecommendationsSection />
      <Applications />
    </>
  );
};

const DesktopDashboard = ({ recentSearches }: Props) => {
  return (
    <>
      <div className="dashboard__col1">
        <JobSearchWidget recentSearches={recentSearches} />
        <JobRecommendationsSection />
        <Applications />
      </div>
      <div className="dashboard__col2">
        <ProfileCard />
      </div>
    </>
  );
};

const stripWelcomeParamFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete("welcome");
  if (url.href === window.location.href) return;

  window.history.replaceState(window.history.state, "", url.toString());
};

const stripWelcomeParamAfterInertiaSettles = (): (() => void) => {
  const stripOnce = () => {
    stripWelcomeParamFromUrl();
    document.removeEventListener("inertia:navigate", stripOnce);
  };
  document.addEventListener("inertia:navigate", stripOnce);

  return () => document.removeEventListener("inertia:navigate", stripOnce);
};

const Dashboard = (props: Props) => {
  const {
    firstName,
    landedDreamJob,
    newUser,
    closeOnLoad,
    showWelcomeModal,
    recentSearches,
    dreamJobBanner,
  } = props;

  const { t } = useTranslation("dashboard");

  const device = useDevice();
  const { uploadResumeModal } = usePage<PageProps>().props;
  const { data: profile, isPending: profilePending } = useProfile();
  const [showUploadResumeModal, setShowUploadResumeModal] = useState(false);
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [welcomeDecided, setWelcomeDecided] = useState(!showWelcomeModal);
  const verificationConfirmationOpen = useRef(
    new URLSearchParams(window.location.search).get("verification_session_complete") === "true",
  );

  useEffectOnce(() => {
    if (!showWelcomeModal) return;

    stripWelcomeParamFromUrl();
    return stripWelcomeParamAfterInertiaSettles();
  });

  useEffect(() => {
    if (!showWelcomeModal || welcomeDecided || profilePending) {
      return;
    }
    setWelcomeDecided(true);

    const uploadResumeModalEligible =
      !!uploadResumeModal && !profile?.resume && !localStorage.getItem("uploadResumeModalShown");

    if (uploadResumeModalEligible || verificationConfirmationOpen.current) {
      return;
    }

    setWelcomeModalOpen(true);
  }, [showWelcomeModal, welcomeDecided, profilePending, profile?.resume, uploadResumeModal]);

  useEffect(() => {
    if (
      !welcomeDecided ||
      !uploadResumeModal ||
      profile?.resume ||
      welcomeModalOpen ||
      localStorage.getItem("uploadResumeModalShown")
    ) {
      return;
    }

    const timer = setTimeout(() => {
      localStorage.setItem("uploadResumeModalShown", "true");
      setShowUploadResumeModal(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [welcomeDecided, uploadResumeModal, profile?.resume, welcomeModalOpen]);

  useEffectOnce(() => {
    if (closeOnLoad) {
      window.close();
    }
  });

  const dashboardTitle = () => {
    if (newUser) {
      return t("header.title_new_user");
    }

    return firstName ? t("header.title_with_name", { name: firstName }) : t("header.title");
  };

  const dashboardSubtitle = () => {
    if (landedDreamJob) {
      return t("header.subtitle_dream_job");
    }

    if (newUser) {
      return t("header.subtitle_new_user");
    }

    const subtitles = t("header.subtitle", { returnObjects: true }) as string[];
    return subtitles[Math.floor(Math.random() * subtitles.length)];
  };

  const content = (
    <>
      {dreamJobBanner ? (
        <DreamJobBanner state={dreamJobBanner.state} count={dreamJobBanner.count} />
      ) : (
        <div className="welcome-banner with-search">
          <div className="welcome-container">
            <div className="welcome-text-wrapper">
              <div className="welcome-text">
                <SectionHeader serif large as="h1" className="welcome-title">
                  {dashboardTitle()}
                </SectionHeader>
                <SectionHeader className="welcome-subtitle">{dashboardSubtitle()}</SectionHeader>
              </div>
            </div>
            <SearchSection recentSearches={recentSearches} />
            <img src={Fingerprint15} alt="Welcome Banner Image" className="welcome-banner__image" />
          </div>
          <img src={Fingerprint15} alt="Welcome Banner Image" className="welcome-banner__image" />
        </div>
      )}
      <div className="dashboard">
        <ErrorBoundary fallback={null}>
          <Suspense fallback={null}>
            <IdentityVerificationBanner />
          </Suspense>
        </ErrorBoundary>
        <SurveyCallout />
        <main>
          {device.isMobile ? <MobileDashboard {...props} /> : <DesktopDashboard {...props} />}
        </main>
      </div>
      <DashboardUploadResumeModal
        open={showUploadResumeModal}
        onOpenChange={setShowUploadResumeModal}
      />
      <OnboardingWelcomeModal open={welcomeModalOpen} onOpenChange={setWelcomeModalOpen} />
    </>
  );

  return content;
};

export default Dashboard;
